import { select, take, race } from "redux-saga/effects";
import { getCurrentUserIsAdmin } from "legacy/selectors/usersSelectors";
import {
  getAgentsSaga,
  isLoadingAgentsHealth,
  selectActiveAgents,
  selectAgentsHealthStatus,
} from "store/slices/agents";
import { orgIsCloud } from "store/slices/organizations/utils";
import { callSagas } from "store/utils/saga";
import { noActiveAgentMessage } from "utils/error/error";
import { sendNoActiveAgentUINotification } from "utils/notification";
import type {
  Agent,
  AgentsHealthStatus,
  Organization,
  Profile,
} from "@superblocksteam/shared";
import type {
  ApiExecutionSagaPayload,
  ApiExecutionResponseDto,
} from "store/slices/apis/types";

export function* getActiveAgents({
  organization,
  environment,
  profile,
  sendNotification = true,
}: {
  organization: Organization;
  environment: ApiExecutionSagaPayload["environment"];
  profile?: Profile;
  sendNotification?: boolean;
}) {
  // Cloud orgs don't have agents
  if (orgIsCloud(organization)) {
    return [];
  }
  let agents: Agent[] = yield select(
    selectActiveAgents(organization.agentType, profile?.key ?? environment),
  );

  if (!agents.length) {
    const isLoadingAgents: boolean = yield select(isLoadingAgentsHealth);
    if (isLoadingAgents) {
      yield race({
        s: take(getAgentsSaga.success.type),
        f: take(getAgentsSaga.error.type),
      });
    } else {
      yield callSagas([getAgentsSaga.apply({ organization })]);
    }

    // Try again
    agents = yield select(
      selectActiveAgents(organization.agentType, profile?.key ?? environment),
    );
    // If still no agents, show error
    if (!agents.length) {
      const userIsAdmin: ReturnType<typeof getCurrentUserIsAdmin> =
        yield select(getCurrentUserIsAdmin);
      const agentsHealthStatus: AgentsHealthStatus = yield select(
        selectAgentsHealthStatus(
          organization.agentType,
          profile?.key ?? environment,
        ),
      );

      sendNotification &&
        sendNoActiveAgentUINotification({
          organizationAgentType: organization.agentType,
          userIsAdmin,
          agentsHealthStatus,
          profile,
        });
      return {
        systemError: noActiveAgentMessage(organization.agentType),
      } as ApiExecutionResponseDto;
    }
  }

  return agents;
}
